export const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

/**
 * Format a price in the given market's currency.
 * Falls back to INR formatting for the default market or when market is not provided.
 */
export const formatMarketPrice = (price, market) => {
  if (!market || market.isDefault) return formatPrice(price);
  const code = market.currencyCode ?? 'USD';
  // INR and other zero-decimal display currencies
  const zeroDecimal = ['INR', 'JPY', 'KRW'].includes(code.toUpperCase());
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: zeroDecimal ? 0 : 2,
      maximumFractionDigits: zeroDecimal ? 0 : 2,
    }).format(price);
  } catch {
    const sym = market.currencySymbol ?? '$';
    return `${sym}${Number(price).toFixed(zeroDecimal ? 0 : 2)}`;
  }
};

/**
 * Format a price using an explicit currency code (e.g. from an order's `currency` field).
 * Falls back to INR when currency is blank/null.
 */
export const formatOrderAmount = (price, currency) => {
  const code = (currency || 'INR').toUpperCase();
  const zeroDecimal = ['INR', 'JPY', 'KRW'].includes(code);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: zeroDecimal ? 0 : 2,
      maximumFractionDigits: zeroDecimal ? 0 : 2,
    }).format(price);
  } catch {
    return `${code} ${Number(price).toFixed(zeroDecimal ? 0 : 2)}`;
  }
};

export const calcDiscount = (price, compareAtPrice) =>
  compareAtPrice > price ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100) : 0;

export const calcSavings = (price, compareAtPrice) =>
  compareAtPrice > price ? compareAtPrice - price : 0;

export const generateOrderId = () =>
  `SKT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;

const MARKET_LOCALE = { IN: 'en-IN', US: 'en-US', GB: 'en-GB', AE: 'en-AE' };

// Advance date past weekends so estimated delivery always lands on a business day.
const nextBusinessDay = (date) => {
  const day = date.getDay(); // 0=Sun, 6=Sat
  if (day === 6) date.setDate(date.getDate() + 2); // Sat → Mon
  else if (day === 0) date.setDate(date.getDate() + 1); // Sun → Mon
  return date;
};

export const getEstimatedDelivery = (paymentMethod, market) => {
  const code   = market?.code ?? 'IN';
  const locale = MARKET_LOCALE[code] ?? 'en-US';
  // India: COD upper bound 4 days, Prepaid upper bound 4 days.
  // International: upper bound 7 days. Weekend landing → pushed to Monday.
  const days = market?.isDefault ?? true
    ? (paymentMethod === 'COD' ? 4 : 4)
    : 7;
  const date = new Date();
  date.setDate(date.getDate() + days);
  nextBusinessDay(date);
  return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
};

export const truncate = (str, n) => str.length > n ? str.substring(0, n) + '...' : str;

export const slugify = (str) => str.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
